
import { VoiceCommand } from '@/types/voiceCommand';
import { useVoiceCommands } from '@/contexts/VoiceCommandContext';
import { useToast } from '@/hooks/use-toast';
import { addDays, addWeeks, format, parseISO, startOfDay, addHours } from 'date-fns';
import { useState, useEffect } from 'react';

export const useCalendarVoiceCommands = (
  createEvent?: (eventDetails: {
    title: string;
    start: Date;
    end: Date;
    description?: string;
    attendees?: string[];
  }) => void
) => {
  const { registerCommand, unregisterCommand } = useVoiceCommands();
  const { toast } = useToast();
  
  useEffect(() => {
    if (!createEvent) return;
    
    const calendarCommands: VoiceCommand[] = [
      {
        name: 'schedule-event',
        phrases: ['schedule', 'create event', 'add event', 'new event', 'book'],
        category: 'calendar',
        action: (params) => {
          if (!params) {
            toast({
              title: "Calendar",
              description: "Please specify what event you'd like to schedule",
              variant: "destructive"
            });
            return;
          }
          
          // Extract event details using simple heuristics
          let title = params;
          let start = new Date();
          let end = new Date();
          end.setHours(end.getHours() + 1); // Default 1 hour duration
          let description = '';
          
          // Extract date information
          if (params.includes('tomorrow')) {
            start = addDays(startOfDay(new Date()), 1);
            end = addHours(start, 1);
            title = title.replace(/tomorrow/gi, '').trim();
          } else if (params.includes('next week')) {
            start = addWeeks(startOfDay(new Date()), 1);
            end = addHours(start, 1);
            title = title.replace(/next week/gi, '').trim();
          } else if (params.includes('today')) {
            start = addHours(startOfDay(new Date()), 1); // 1 hour from now
            end = addHours(start, 1);
            title = title.replace(/today/gi, '').trim();
          }
          
          // Extract time information (very simple heuristic)
          const timeMatch = params.match(/at\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
          if (timeMatch) {
            let hours = parseInt(timeMatch[1]);
            const minutes = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
            const meridiem = timeMatch[3]?.toLowerCase();
            
            // Adjust hours for pm
            if (meridiem === 'pm' && hours < 12) {
              hours += 12;
            } else if (meridiem === 'am' && hours === 12) {
              hours = 0;
            }
            
            start.setHours(hours, minutes, 0);
            end = new Date(start);
            end.setHours(end.getHours() + 1);
            
            // Remove the time part from the title
            title = title.replace(/at\s+\d{1,2}(?::\d{2})?\s*(?:am|pm)?/i, '').trim();
          }
          
          // Create the event
          createEvent({
            title,
            start,
            end,
            description
          });
          
          toast({
            title: "Event Scheduled",
            description: `Event "${title}" has been scheduled for ${format(start, 'PPp')}`
          });
        },
        description: "Schedule a new event in your calendar",
        examples: [
          "Schedule meeting with John tomorrow at 2pm",
          "Create event record vocals next week",
          "Add event band practice today at 7pm"
        ]
      }
    ];
    
    // Register commands
    calendarCommands.forEach(cmd => registerCommand(cmd));
    
    // Cleanup
    return () => {
      calendarCommands.forEach(cmd => unregisterCommand(cmd.name));
    };
  }, [registerCommand, unregisterCommand, toast, createEvent]);
};
